import type { RootStackParamList } from "../../../../../../navigation/types";
import type { InferenceMethod } from "../../../requirements/TaxonomyEntity";

import { MinimumPayForm, type SalaryPreference } from "./forms/MinimumPayForm";
import { SentimentForm } from "./forms/SentimentForm";
import { styles } from "./styles";
import { useCollectionModalController } from "./useCollectionModalController";
import { Box, Button, ScrollView } from "../../../../../../../../mads";
import { Text } from "../../../../../../../../mads/Text";
import Sentiment from "../../../../../../../../shared/util/salary/Sentiment";
import { useModalContext } from "../../../context/JMUIModalContextProvider";
import { MarkupWrapper } from "../MarkupWrapper";
import { QuestionText } from "../QuestionText";
import { stripMarkup } from "../utils/stripMarkup";

import { type RouteProp, useNavigation } from "@react-navigation/native";
import React, { useEffect, useState } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type DataT = {
  sentiment: Sentiment;
  salaryPreference?: SalaryPreference;
};

export type CollectionModalProps = {
  isOpen?: boolean;
  onModalReset: () => Promise<boolean>;
  onClose?: () => void;

  title: string;
  question: string;
  editDisclaimer?: string;

  inferenceMethod?: InferenceMethod;
  showSalary?: boolean;

  data: DataT;
  onModalSubmit: (data: DataT) => Promise<boolean>;
  setData?: (d: Partial<DataT>) => void;
};

export type CollectionModalScreenRouteProps = {
  route: RouteProp<RootStackParamList, "CollectionModal">;
};

export function CollectionModal({ route }: CollectionModalScreenRouteProps) {
  const { question, editDisclaimer, inferenceMethod, showSalary, data } = route.params;

  const navigation = useNavigation();
  const { collectionModalCallbacksRef } = useModalContext();
  const [footerHeight, setFooterHeight] = useState(0);
  const { bottom } = useSafeAreaInsets();

  const {
    loading,
    hasSubmitted,
    modalData,
    setModalData,
    originalSentiment,
    footerButtons,
    handleSave,
    handleClose,
  } = useCollectionModalController({
    data,
    inferenceMethod,
    showSalary,
    closeModal: () => navigation.goBack(),
  });

  // Restore focus to the tile that opened this modal when it closes
  useEffect(() => {
    return () => {
      // Access ref.current at cleanup time to get the latest callback, not at effect setup time
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally access ref.current at cleanup time
      collectionModalCallbacksRef.current.onCleanup?.();
    };
  }, [collectionModalCallbacksRef]);

  return (
    <Box sx={styles.modalContainer}>
      <ScrollView
        sx={[styles.modalContent, { marginBottom: footerHeight + bottom }]}
        contentContainerSx={styles.scrollContent}
      >
        <Box sx={{ marginBottom: 4 }}>
          <Text level={4} variant="contrast" data-testid="modal-question">
            <QuestionText>{question}</QuestionText>
          </Text>
          {editDisclaimer && originalSentiment === Sentiment.POSITIVE && (
            <Text level={2} variant="secondary">
              <MarkupWrapper label={editDisclaimer} sx={{ fontWeight: "bold" }} />
            </Text>
          )}
        </Box>
        {showSalary ? (
          <MinimumPayForm
            value={modalData.salaryPreference}
            onChange={(s) => {
              setModalData({ ...modalData, salaryPreference: s });
            }}
            showErrors={hasSubmitted}
          />
        ) : (
          <SentimentForm
            isLoading={loading}
            onChange={(d) => {
              setModalData({ ...modalData, ...d });
            }}
            value={modalData}
            handleSave={handleSave}
            onClose={handleClose}
            accessibilityQuestionText={stripMarkup(question)}
          />
        )}
      </ScrollView>
      <Box
        sx={[styles.footerButtons, { bottom: bottom || 0 }]}
        onLayout={(event) => {
          const { height } = event.nativeEvent.layout;
          setFooterHeight(height);
        }}
      >
        {footerButtons?.map((buttonProps, index) => {
          return (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            <Button key={index} {...buttonProps.props} sx={{ width: "100%" }}>
              {}
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access */}
              {buttonProps.label}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

export default CollectionModal;
